<template>
  <div class="renderPage nce-container" :class="{ 'theme--dark': $vuetify.theme.dark }">
    <!-- TOOLBAR CABECERA -->
    <v-toolbar flat height="56" :color="$vuetify.theme.dark ? '#1e293b' : '#f1f5f9'" class="nce-toolbar px-2">
      <v-icon class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-file-document-edit-outline</v-icon>
      <v-toolbar-title class="font-weight-black text-uppercase nce-toolbar-title" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">
        Notas de Crédito
      </v-toolbar-title>
      <v-spacer />
      <v-btn depressed color="primary" class="nce-btn font-weight-bold" :disabled="!incluirValid" @click="validarLicencia()">
        <v-icon left small>mdi-plus</v-icon> Nueva Nota de Crédito
      </v-btn>
    </v-toolbar>

    <!-- FILTROS Y TABLA DE HISTORIAL -->
    <v-card flat class="nce-table-card mx-0" :color="$vuetify.theme.dark ? '#0f172a' : 'white'">
      <v-toolbar flat height="auto" :color="$vuetify.theme.dark ? '#0f172a' : 'white'" class="px-3 pt-2 pb-3">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <v-dialog
              ref="dialog"
              v-model="dateModal"
              :return-value.sync="dates"
              persistent
              width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="datesFormat"
                  label="Rango de fechas"
                  prepend-inner-icon="mdi-calendar"
                  readonly outlined dense hide-details
                  class="nce-search"
                  v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dates" range scrollable color="primary">
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="dateModal = false">Cancelar</v-btn>
                <v-btn text color="primary" @click="$refs.dialog.save(dates); setDatesFormat(); getData();">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-col>
          <v-col cols="12" sm="5" md="5">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Buscar por cliente, código o estado..."
              single-line hide-details outlined dense
              class="nce-search"
            ></v-text-field>
          </v-col>
          <v-spacer></v-spacer>
          <v-btn icon color="primary" @click="getData()">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-row>
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :items="items"
        :search="search"
        class="nce-table"
        :loading="loading"
        @click:row="editItem"
        style="cursor:pointer"
      >
        <template v-slot:[`item.estadoHacienda`]="{ item }">
          <v-chip
            small
            :color="getEstadoColor(item.estadoHacienda)"
            class="font-weight-bold"
            text-color="white"
          >
            {{ item.estadoHacienda }}
          </v-chip>
        </template>

        <template v-slot:[`item.totalComprobante`]="{ item }">
          <span class="font-weight-bold">{{ (item.totalComprobante || 0).toLocaleString('es-CR', { style: 'currency', currency: 'CRC' }) }}</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn color="blue-grey darken-2" depressed dark style="min-width:36px;height:36px;padding:0;border-radius:4px" @click.stop="editItem(item)">
            <v-icon size="18">mdi-eye-outline</v-icon>
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="pa-8 text-center">
            <v-icon size="48" :color="$vuetify.theme.dark ? 'blue-grey darken-1' : 'grey lighten-1'" class="mb-3">mdi-file-document-outline</v-icon>
            <p :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No se encontraron notas de crédito en este periodo.</p>
            <v-btn color="primary" depressed small @click="getData()">Reintentar</v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- FORM DRAWER - "SUPER DRAWER" PARA NOTAS DE CRÉDITO -->
    <v-navigation-drawer
      v-model="drawerForm"
      fixed right temporary
      :width="$vuetify.breakpoint.mdAndUp ? 'calc(100% - 290px)' : '100%'"
      class="nce-drawer"
      stateless
    >
      <div class="d-flex flex-column fill-height">
        <!-- HEADER DEL DRAWER -->
        <div class="nce-drawer-header d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'nce-drawer-header--dark' : ''">
          <v-icon color="white" class="mr-3">mdi-file-document-edit-outline</v-icon>
          <div>
            <div class="white--text font-weight-black text-uppercase" style="letter-spacing:1px;font-size:0.95rem;">
              {{ editedIndex === -1 ? 'Nueva Nota de Crédito' : 'Detalles de la Nota' }}
            </div>
            <div class="caption white--text" style="opacity:0.7">
              {{ editedItem.codigoFact ? 'Comprobante #' + editedItem.codigoFact : 'Generando nuevo documento...' }}
              <v-chip v-if="editedIndex > -1 && editedItem.estadoHacienda" x-small dark outlined class="ml-2 font-weight-black" style="height:18px">
                {{ editedItem.estadoHacienda }}
              </v-chip>
            </div>
          </div>
          <v-spacer />
          <v-btn icon dark small @click="cerrar()"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <!-- DRAWER CONTENT -->
        <div class="flex-grow-1 overflow-y-auto px-2 py-2 bg-light">
          <v-form v-model="valid" ref="formDrawer">
                <v-expansion-panels v-model="activePanels" multiple class="modern-panels mb-6">

                  <!-- SECCIÓN 1: DOCUMENTO ORIGEN -->
                  <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light">
                    <v-expansion-panel-header class="font-weight-bold primary--text">
                      <v-icon color="primary" left class="mr-3">mdi-file-link-outline</v-icon>
                      Documento de Referencia
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="panel-content-compact">
                      <v-row dense align="center">
                        <v-col cols="12" md="7">
                          <v-text-field
                            v-model="editedItem.codigoDev"
                            label="Clave Hacienda del Documento Origen"
                            outlined dense hide-details
                            readonly
                            :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"
                            prepend-inner-icon="mdi-key-variant"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model="editedItem.documentoOrigen"
                            label="Documento"
                            outlined dense hide-details
                            readonly
                            :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="2">
                          <v-btn
                            color="primary" depressed block
                            height="40"
                            @click="mostrarBuscarDocumentos"
                            :disabled="!btnAgregarDocumento"
                            class="font-weight-bold nce-btn"
                          >
                            <v-icon left>mdi-file-search</v-icon>
                            Buscar
                          </v-btn>
                        </v-col>
                      </v-row>
                      <v-row dense class="mt-3" v-if="editedItem.emisorNombre">
                        <v-col cols="12" md="6">
                          <v-text-field v-model="editedItem.emisorNombre" label="Emisor Autorizado" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'" prepend-inner-icon="mdi-office-building"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field v-model="editedItem.moneda" label="Moneda" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'" prepend-inner-icon="mdi-cash-multiple"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field v-model="editedItem.tipoCambio" label="T. Cambio" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'" prepend-inner-icon="mdi-trending-up"></v-text-field>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- SECCIÓN 2: RECEPTOR -->
                  <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light" v-if="editedItem.clienteNombre">
                    <v-expansion-panel-header class="font-weight-bold primary--text">
                      <v-icon color="primary" left class="mr-3">mdi-account-details-outline</v-icon>
                      Información del Receptor
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="panel-content-compact">
                      <v-row dense>
                        <v-col cols="12" md="2">
                          <v-text-field v-model="editedItem.clienteCod" label="# Cliente" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field v-model="editedItem.clienteCedula" label="Cédula" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field v-model="editedItem.clienteNombre" label="Nombre/Razón Social" outlined dense hide-details readonly :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field v-model="editedItem.clientecorreo" label="Correo Notificación" outlined dense hide-details prepend-inner-icon="mdi-email-outline"></v-text-field>
                        </v-col>
                        <v-col cols="12" v-if="actividadesCliente.length > 0">
                          <v-select
                            v-model="editedItem.clienteCodigoActividad"
                            :items="actividadesCliente"
                            item-text="descripcion"
                            item-value="codigo"
                            label="Actividad Económica del Receptor"
                            outlined dense hide-details
                            prepend-inner-icon="mdi-briefcase-outline"
                          ></v-select>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- SECCIÓN 3: LÍNEAS DE DETALLE -->
                  <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light">
                    <v-expansion-panel-header class="font-weight-bold primary--text">
                      <div class="d-flex align-center w-100">
                        <v-icon color="primary" left class="mr-3">mdi-clipboard-list-outline</v-icon>
                        Líneas de Nota de Crédito
                        <v-spacer></v-spacer>
                        <v-chip v-if="lineasDetalle.length > 0" small color="primary" class="font-weight-black px-3">{{ lineasDetalle.length }} Ítems</v-chip>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="pt-0">
                      <v-data-table
                        :headers="headersDetalle"
                        :items="lineasDetalle"
                        hide-default-footer
                        class="detail-table-modern border-light rounded-xl overflow-hidden"
                        :items-per-page="-1"
                      >
                        <template v-slot:[`item.cantidad`]="{ item }">

                          <v-text-field
                            v-model.number="item.cantidad"
                            type="number" min="0" :max="item.cantidadMax"
                            outlined dense hide-details
                            class="compact-input"
                            @input="changeCantidad"
                            :readonly="!guardarValid"
                          ></v-text-field>
                        </template>
                        <template v-slot:[`item.montoStr`]="{ item }">
                          <span class="font-weight-bold">{{ item.montoStr }}</span>
                        </template>
                        <template v-slot:[`item.totalLineaStr`]="{ item }">
                          <span class="font-weight-black primary--text">{{ item.totalLineaStr }}</span>
                        </template>
                        <template v-slot:[`item.edit`]="{ item }">
                          <v-btn icon small color="primary" @click="detalleEditItem(item)" v-if="guardarValid">
                            <v-icon small>mdi-pencil-outline</v-icon>
                          </v-btn>
                        </template>
                        <template v-slot:[`item.delete`]="{ item }">
                          <v-btn icon small color="error" @click="deleteItem(item)" v-if="guardarValid">
                            <v-icon small>mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </template>
                      </v-data-table>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- SECCIÓN 4: TOTALES -->
                  <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light">
                    <v-expansion-panel-header class="font-weight-bold primary--text">
                      <v-icon color="primary" left class="mr-3">mdi-calculator</v-icon>
                      Cálculos y Totales
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="panel-content-compact">
                      <v-row dense>
                        <v-col cols="12" md="3">
                          <v-card flat class="rounded-xl pa-2 bg-light border-light text-center">
                            <div class="caption grey--text font-weight-bold text-uppercase mb-1">Subtotal</div>
                            <div class="text-body-2 font-weight-bold">{{ editedItem.subtotalStr || '0.00' }}</div>
                          </v-card>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-card flat class="rounded-xl pa-2 bg-light border-light text-center">
                            <div class="caption grey--text font-weight-bold text-uppercase mb-1">Impuestos</div>
                            <div class="text-body-2 font-weight-bold primary--text">{{ editedItem.totalImpuestoStr || '0.00' }}</div>
                          </v-card>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-card flat class="rounded-xl pa-2 bg-light border-light text-center">
                            <div class="caption grey--text font-weight-bold text-uppercase mb-1">Descuentos</div>
                            <div class="text-body-2 font-weight-bold error--text">{{ editedItem.totalDescuentosStr || '0.00' }}</div>
                          </v-card>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-card flat class="rounded-xl pa-2 primary border-light text-center white--text shadow-primary">
                            <div class="caption opacity-80 mb-0">TOTAL NOTA</div>
                            <div class="text-body-2 font-weight-black">{{ editedItem.totalComprobanteStr || '0.00' }}</div>
                          </v-card>
                        </v-col>
                      </v-row>
                      
                      <v-divider class="my-2"></v-divider>
                      
                      <v-row dense>
                        <v-col cols="6" md="2">
                          <v-text-field v-model="editedItem.totalIva1Str" label="IVA 1%" readonly outlined dense hide-details></v-text-field>
                        </v-col>
                        <v-col cols="6" md="2">
                          <v-text-field v-model="editedItem.totalIva2Str" label="IVA 2%" readonly outlined dense hide-details></v-text-field>
                        </v-col>
                        <v-col cols="6" md="2">
                          <v-text-field v-model="editedItem.totalIva4Str" label="IVA 4%" readonly outlined dense hide-details></v-text-field>
                        </v-col>
                        <v-col cols="6" md="2">
                          <v-text-field v-model="editedItem.totalIva13Str" label="IVA 13%" readonly outlined dense hide-details></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field v-model="editedItem.totalIVADevueltoStr" label="IVA Devuelto" readonly outlined dense hide-details class="right-input" :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"></v-text-field>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                  <!-- SECCIÓN 5: HACIENDA -->
                  <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light">
                    <v-expansion-panel-header class="font-weight-bold primary--text">
                      <div class="d-flex align-center" style="width:100%">
                        <v-icon color="primary" left class="mr-2">mdi-cloud-check-outline</v-icon>
                        Estado y Gestión Hacienda
                        <v-spacer></v-spacer>
                        <v-chip
                          v-if="editedItem.estadoHacienda"
                          :color="getEstadoColor(editedItem.estadoHacienda)"
                          dark x-small
                          class="font-weight-bold mr-2"
                        >
                          {{ editedItem.estadoHacienda }}
                        </v-chip>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row dense align="start">
                        <v-col cols="12" md="8">
                          <v-text-field
                            v-model="editedItem.clavehacienda"
                            label="Clave de Comprobante"
                            readonly outlined dense hide-details
                            :background-color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" class="d-flex align-center">
                          <v-btn
                            color="primary"
                            depressed block height="40"
                            @click="consultarEstadoHacienda"
                            :disabled="!consultarEstadoValid || visibleProgress"
                            :loading="visibleProgress"
                            class="font-weight-bold nce-btn"
                          >
                            <v-icon left>mdi-sync</v-icon>
                            Consultar Estado
                          </v-btn>
                        </v-col>
                        <v-col cols="12" v-if="editedItem.estadoHacienda" class="mt-2">
                          <div class="d-flex align-center pa-2 rounded-lg bg-light border-light">
                            <v-icon :color="getEstadoColor(editedItem.estadoHacienda)" class="mr-2">mdi-information-outline</v-icon>
                            <span class="font-weight-bold mr-2">Estado:</span>
                            <v-chip dark small :color="getEstadoColor(editedItem.estadoHacienda)" class="font-weight-black">
                              {{ editedItem.estadoHacienda }}
                            </v-chip>
                          </div>
                        </v-col>
                        <v-col cols="12">
                          <v-textarea
                            v-model="editedItem.mensajeHacienda"
                            label="Respuesta de Hacienda"
                            readonly outlined rows="2"
                            class="mt-2"
                            hide-details
                          ></v-textarea>
                        </v-col>
                      </v-row>

                      <v-row v-if="editedIndex > -1" class="border-top-light pt-4 px-2 mt-2" justify="center">
                        <v-btn text color="primary" class="mx-2 rounded-pill pr-4" @click="getReport()" :disabled="!verPDF">
                          <v-icon left>mdi-file-pdf-box</v-icon> Ver PDF
                        </v-btn>
                        <v-btn text color="primary" class="mx-2 rounded-pill pr-4" @click="getXMLFirmado()" :disabled="!verXMLFirmado">
                          <v-icon left>mdi-xml</v-icon> XML Enviado
                        </v-btn>
                        <v-btn text color="primary" class="mx-2 rounded-pill pr-4" @click="getXMLRespuesta()" :disabled="!verXMLRespuesta">
                          <v-icon left>mdi-message-reply-text</v-icon> Respuesta XML
                        </v-btn>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- SECCIÓN 6: PARÁMETROS DE EMISIÓN -->
                  <v-expansion-panel class="rounded-xl mb-0 elevation-0 border-light">
                    <v-expansion-panel-header class="font-weight-bold primary--text">
                      <div class="d-flex align-center" style="width:100%">
                        <v-icon color="primary" left class="mr-2">mdi-cog-outline</v-icon>
                        Parámetros de Emisión
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row dense>
                        <v-col cols="12" md="6">
                          <v-select label="Condición Venta" v-model="editedItem.condicionVenta" :items="condicionesVenta" item-text="descripcion" item-value="codigo" outlined dense hide-details></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-select label="Medio de Pago" v-model="editedItem.medioPago" :items="mediosPago" item-text="descripcion" item-value="codigo" outlined dense hide-details></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-select
                            v-model="editedItem.codigoActividad"
                            :items="actividades"
                            item-text="descripcion"
                            item-value="codigo"
                            label="Actividad Económica del Emisor"
                            outlined dense hide-details
                            prepend-inner-icon="mdi-briefcase-outline"
                          ></v-select>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field v-model="editedItem.ordenCompra" label="Orden de Compra #" outlined dense prepend-inner-icon="mdi-file-document-outline" hide-details></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-textarea v-model="editedItem.observacion" label="Observaciones" outlined rows="2" hide-details></v-textarea>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                </v-expansion-panels>
              </v-form>
            </div>

            <!-- FOOTER ACCIONES -->
            <div class="nce-drawer-footer d-flex align-center px-4 py-3" :class="$vuetify.theme.dark ? 'nce-drawer-footer--dark' : ''">
              <v-btn v-if="reenviarValid" color="secondary" text class="nce-btn font-weight-bold" @click="reenviar">
                <v-icon left small>mdi-send-outline</v-icon> Reenviar Nota
              </v-btn>
              <v-spacer />
              <v-btn text class="mr-3 font-weight-bold nce-btn" @click="cerrar()">Cancelar</v-btn>
              <v-btn color="primary" depressed class="px-8 font-weight-bold nce-btn" :loading="visibleProgress" @click="save" :disabled="!guardarValid">
                {{ editedIndex === -1 ? 'GENERAR NOTA' : 'GUARDAR CAMBIOS' }}
              </v-btn>
            </div>
          </div>
        </v-navigation-drawer>


    <!-- VISOR DE DOCUMENTOS (PDF / XML) -->
    <v-dialog v-model="docViewerDialog" max-width="1100" eager>
      <v-card style="height: 88vh;" class="d-flex flex-column overflow-hidden rounded-xl">
        <div class="doc-viewer-toolbar d-flex align-center px-3 py-2 white--text flex-shrink-0">
          <v-icon color="white" size="18" class="mr-2">mdi-file-document-outline</v-icon>
          <span class="font-weight-bold text-subtitle-2">{{ docViewerTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn icon x-small dark class="mr-1" @click="abrirEnNuevaPestana" title="Abrir en nueva pestaña">
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn icon x-small dark @click="docViewerDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <iframe v-if="docViewerDialog" :src="docViewerUrl" class="flex-grow-1" style="border: none; width: 100%;"></iframe>
      </v-card>
    </v-dialog>

    <!-- MODAL DE BÚSQUEDA DE DOCUMENTOS -->
    <BuscarDocumentos 
      ref="buscarDocs"
      :dialog="btnAgregarDocumento"
      @seleccionar="selected"
      @cerrar="btnAgregarDocumento = false"
    />

    <!-- MODAL DE REENVÍO (MODERNO) -->
    <v-dialog v-model="dialogFormaReenvio" max-width="450px" persistent>
      <v-card class="rounded-xl overflow-hidden">
        <v-toolbar flat color="primary" dark class="px-2">
          <v-toolbar-title class="font-weight-bold">
            <v-icon left>mdi-email-send-outline</v-icon>
            Reenviar Documento
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogFormaReenvio = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        
        <v-card-text class="pa-6">
          <p class="text-body-1 grey--text text--darken-2">
            Se enviará el comprobante electrónico a la siguiente dirección de correo vinculada al cliente.
          </p>
          
          <v-text-field
            v-model="editedItem.clientecorreo"
            label="Correo Electrónico"
            outlined rounded
            prepend-inner-icon="mdi-email-outline"
            class="modern-input mt-4"
          ></v-text-field>
          
          <v-alert
            type="info"
            text
            dense
            class="rounded-lg mt-2 mb-0"
            icon="mdi-information-outline"
          >
            Asegúrese de que el correo sea correcto antes de continuar.
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text rounded large class="px-6 font-weight-bold grey--text" @click="dialogFormaReenvio = false">Cancelar</v-btn>
          <v-btn
            color="primary" rounded large elevation="4"
            class="px-10 font-weight-bold shadow-primary"
            @click="enviarCorreo()"
          >
            ENVIAR AHORA
            <v-icon right small>mdi-send-check</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";
import BuscarDocumentos from "./BuscarDocumentos.vue";
//import BuscarArticulos from "./BuscarArticulos.vue";

export default {
  components: {
    Snackbar,
    BuscarDocumentos,
    //BuscarArticulos,
  },
  // dark: true,
  data() {
    return {
      drawerForm: false,
      activePanels: [0, 2, 3],
      btnAgregarDocumento: false,
      btnAgregarArti: false,
      iconVisible: false,
      iconVisibleCedula: false,
      visibleBtnConsultar: true,
      loading: false,
      visibleProgress: false,
      dialogArticulos: false,
      valid: false,
      incluirValid: true,
      consultarEstadoValid: false,
      cancelarValid: false,
      reenviarValid: false,
      imprimirValid: false,
      docViewerDialog: false,
      docViewerUrl: '',
      docViewerTitle: '',
      guardarValid: false,
      dialogFormaReenvio: false,
      search: "",
      snackText: "",
      snackValue: false,
      dialogDelete: false,
      verPDF: false,
      verXMLFirmado: false,
      verXMLRespuesta: false,
      panel: [],
      dateModal: false,
      dates: [],
      datesFormat: [],
      panelResumen: [],
      //selectedDetalles: [],
      headers: [
        {
          text: "Código",
          align: "start",
          value: "codigoFact",
        },
        {
          text: "Cliente",
          align: "start",
          value: "clienteNombre",
        },
        {
          text: "Fecha",
          align: "start",
          value: "fechaString",
        },
        {
          text: "Clave",
          align: "start",
          value: "clavehacienda",
        },
        {
          text: "Estado",
          align: "start",
          value: "estadoHacienda",
        },
        { text: "Total", align: "end", value: "totalComprobante" },
        { text: "Editar", value: "actions", width: 50, sortable: false },
      ],
      headersDetalle: [
        {
          text: "Cant",
          align: "start",
          value: "cantidad",
        },
        {
          text: "Código",
          align: "start",
          value: "articulo",
        },
        {
          text: "Descripción",
          align: "start",
          value: "descripcion",
        },
        {
          text: "Unidad",
          align: "start",
          value: "unidad",
        },
        {
          text: "Precio",
          align: "start",
          value: "montoStr",
        },
        {
          text: "Total",
          align: "start",
          value: "totalLineaStr",
        },
        { text: "", value: "edit", width: 50, sortable: false },
        { text: "Eliminar", value: "delete", width: 70, sortable: false },
      ],
      items: [],
      tiposIdentificacion: [],
      actividades: [],
      actividadesCliente: [],
      condicionesVenta: [],
      mediosPago: [],
      sucursales: [],
      terminales: [],
      monedas: [],
      provincias: [],
      cantones: [],
      distritos: [],
      barrios: [],
      cliente: [],
      articulo: [],
      totales: [],
      lineasDetalle: [],
      tipoCambio: [],
      respuestaHacienda: [],
      comprobante: [],
      editedIndex: -1,
      modelFactura: {
        comprobante: [],
        comprobanteDetalle: [],
      },
      editedItem: {
        cia: "",
        codigoFact: "",
        tipoFact: "",
        fecha: new Date().toISOString().slice(0, 10),
        tipoCambio: 1,
        clienteCod: "",
        clienteNombre: "",
        clienteCorreo: "",
        clienteCedula: "",
        clienteAreaTel: "",
        clienteTelefono: "",
        clienteNombreComercial: "",
        clienteProvincia: "",
        clienteCanton: "",
        clienteDistrito: "",
        clienteBarrio: "",
        clienteDireccion: "",
        clienteTipoId: "",
        mensajeHacienda: "",
        moneda: "",
        estadoHacienda: "",
        clavehacienda: "",
        subtotal: 0,
        totalDescuentos: 0,
        totalImpuesto: 0,
        totalComprobante: 0,
        codigoDev: "",
        estadoDev: 0,
        consHacienda: "",
        condicionVenta: "",
        medioPago: "",
        plazoCredito: 0,
        sucursal: "",
        terminal: "",
        xml: "",
        xmlFirmado: "",
        respuestaHacienda: "",
        fechaHacienda: Date,
        totalGravado: 0,
        totalExento: 0,
        consHacReal: "",
        cedulaReceptor: "",
        ordenCompra: "",
        observacion: "",
        usuario: "",
        enviado: false,
        reenviado: false,
        totalServGravados: 0,
        totalServExentos: 0,
        totalServExonerado: 0,
        totalMercanciasExentas: 0,
        totalMercanciasGravadas: 0,
        totalMercExonerada: 0,
        totalExonerado: 0,
        totalVenta: 0,
        totalVentaNeta: 0,
        totalIVADevuelto: 0,
        totalOtrosCargos: 0,
        totalIva1: 0,
        totalIva2: 0,
        totalIva4: 0,
        totalIva8: 0,
        totalIva13: 0,
        codigoActividad: "",
        clienteCodigoActividad: "",
        emisorNombre: "",
        emisorNombreComercial: "",
        emisorTelefono: "",
        emisorCorreo: "",
        emisorDireccion: "",
        subTotal: 0,
        totalIVADevueltoStr: "",
        totalIva1Str: "",
        totalIva2Str: "",
        totalIva4Str: "",
        totalIva13Str: "",
        totalDescuentosStr: "",
        totalImpuestoStr: "",
        subtotalStr: "",
        totalComprobanteStr: "",
        nombreTabla: "",
        documentoOrigen: "",
        NombreTablaReferencia: "",
        contingenciaFact: "",
        contingenciaFecha: Date,
      },
      defaultItem: {
        cia: "",
        codigoFact: "",
        tipoFact: "",
        fecha: Date,
        tipoCambio: 1,
        clienteCod: "",
        clienteNombre: "",
        clienteCorreo: "",
        clienteCedula: "",
        clienteAreaTel: "",
        clienteTelefono: "",
        clienteNombreComercial: "",
        clienteProvincia: "",
        clienteCanton: "",
        clienteDistrito: "",
        clienteBarrio: "",
        clienteDireccion: "",
        clienteTipoId: "",
        mensajeHacienda: "",
        moneda: "",
        estadoHacienda: "",
        clavehacienda: "",
        subtotal: 0,
        totalDescuentos: 0,
        totalImpuesto: 0,
        totalComprobante: 0,
        codigoDev: "",
        estadoDev: 0,
        consHacienda: "",
        condicionVenta: "",
        medioPago: "",
        plazoCredito: 0,
        sucursal: "",
        terminal: "",
        xml: "",
        xmlFirmado: "",
        respuestaHacienda: "",
        fechaHacienda: Date,
        totalGravado: 0,
        totalExento: 0,
        consHacReal: "",
        cedulaReceptor: "",
        ordenCompra: "",
        observacion: "",
        usuario: "",
        enviado: false,
        reenviado: false,
        totalServGravados: 0,
        totalServExentos: 0,
        totalServExonerado: 0,
        totalMercanciasExentas: 0,
        totalMercanciasGravadas: 0,
        totalMercExonerada: 0,
        totalExonerado: 0,
        totalVenta: 0,
        totalVentaNeta: 0,
        totalIVADevuelto: 0,
        totalOtrosCargos: 0,
        totalIva1: 0,
        totalIva2: 0,
        totalIva4: 0,
        totalIva8: 0,
        totalIva13: 0,
        codigoActividad: "",
        clienteCodigoActividad: "",
        emisorNombre: "",
        emisorNombreComercial: "",
        emisorTelefono: "",
        emisorCorreo: "",
        emisorDireccion: "",
        subTotal: 0,

        totalIVADevueltoStr: "",
        totalIva1Str: "",
        totalIva2Str: "",
        totalIva4Str: "",
        totalIva13Str: "",
        totalDescuentosStr: "",
        totalImpuestoStr: "",
        subtotalStr: "",
        totalComprobanteStr: "",
        nombreTabla: "",
        documentoOrigen: "",
        NombreTablaReferencia: "",
        contingenciaFact: "",
        contingenciaFecha: Date,
      },
      ciaItem: {
        nombre: "",
        nombreComercial: "",
        cedula: "",
        provincia: "",
        canton: "",
        distrito: "",
        barrio: "",
        direccion: "",
        sucursal: "",
        terminal: "",
        moneda: "",
        moneda2: "",
        usuariohacienda: "",
        clavehacienda: "",
        propietarioCert: "",
        certificado: "",
        claveCert: "",
        esProd: false,
        correo: "",
        claveCcorreo: "",
        smtpCorreo: "",
        puertoCorreo: "",
        usaSeguridad: false,
        areaTel: "",
        telefono: "",
        tipoId: "",
        codigoActividad: "",
        image: "",
      },
      detalleditedIndex: -1,
      detalleEditedItem: {
        cia: "",
        factura: "",
        tipoFact: "",
        numLinea: 0,
        cantidad: 0,
        articulo: "",
        descripcion: "",
        unidad: "",
        monto: 0,
        descuento: 0,
        totalLinea: 0,
        impuestosVentas: 0,
        sucursal: "",
        terminal: "",
        tipoCodigo: "",
        unidadMedidaComercial: "",
        impCodigo: "",
        impCodigoTarifa: "",
        impTarifa: "",
        cabys: "",
        porcDescuento: 0,
        tipoCambio: 0,
        tipoProducto: "",
        montoTotal: 0,
        subTotal: 0,
        impuestoNeto: 0,
        moneda: "",
        montoStr: "",
        totalLineaStr: "",
        cant_Dev: 0,
        linea_Dev: 0,
        cantidadMax: 0,
        registroMedicamento: "",
        formaFarmaceutica: "",
      },
      detalleDefaultItem: {
        cia: "",
        factura: "",
        tipoFact: "",
        numLinea: 0,
        cantidad: 0,
        articulo: "",
        descripcion: "",
        unidad: "",
        monto: 0,
        descuento: 0,
        totalLinea: 0,
        impuestosVentas: 0,
        sucursal: "",
        terminal: "",
        tipoCodigo: "",
        unidadMedidaComercial: "",
        impCodigo: "",
        impCodigoTarifa: "",
        impTarifa: "",
        cabys: "",
        porcDescuento: 0,
        tipoCambio: 0,
        tipoProducto: "",
        montoTotal: 0,
        subTotal: 0,
        impuestoNeto: 0,
        moneda: "",
        montoStr: "",
        totalLineaStr: "",
        cant_Dev: 0,
        linea_Dev: 0,
        cantidadMax: 0,
        registroMedicamento: "",
        formaFarmaceutica: "",
      },
      reenvioInfo: {
        numero: "",
        correo: "",
        porwhatsapp: false,
        porcorreo: false,
      },
      showNewDataArti: false,
      nameRules: [(v) => !!v || "Campo requerido"],
      tempFile: "",
      modelFile: null,
    };
  },

  computed: {
    ...mapGetters(["user", "strCia"]),
    formTitle() {
      return this.editedIndex === -1 ? "Agregar Articulo" : "Editar Articulo";
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },

    dialogArticulos(val) {
      val || this.close();
    },

    search() {
      this.snackValue = false;
    },
  },

  created() {
    this.setDates();
    this.getData();
    this.getMonedas();
    this.getProvincias();
    this.getActividades();
    this.getCondicionVenta();
    this.getMedioPago();
  },

  methods: {
    editItem(item) {
      this.snackValue = false;
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.getModeloComprobanteNC();
      this.editedItem.nombreTabla = "NotasCredito";
      this.getCia();
      this.getMonedas();
      this.getActividades();
      this.getCondicionVenta();
      this.getMedioPago();
      this.getSucursales();
      this.changeProvincia();
      this.changeCantones();
      this.changeDistritos();
      
      // Button visibility for existing items
      const accepted = item.estadoHacienda === 'ACEPTADO' || item.estadoHacienda === 'RECHAZADO';
      this.imprimirValid = accepted;
      this.reenviarValid = accepted;
      this.verPDF = accepted;
      this.verXMLFirmado = accepted;
      this.verXMLRespuesta = accepted;
      this.consultarEstadoValid = item.estadoHacienda === 'PROCESANDO';
      
      this.guardarValid = false;
      this.incluirValid = false;
      this.cancelarValid = true;
      this.btnAgregarDocumento = false;
      this.drawerForm = true;
      this.activePanels = [0, 1, 2, 3, 4];
    },

    getEstadoColor(estado) {
      if (!estado) return "grey";
      const e = estado.toUpperCase();
      if (e === "ACEPTADO") return "success";
      if (e === "RECHAZADO") return "error";
      if (e === "PROCESANDO") return "warning";
      if (e === "PENDIENTE") return "info";
      return "grey";
    },

    mostrarBuscarDocumentos() {
      this.btnAgregarDocumento = true;
      this.cancelarValid = true;
    },

    changeCantidad() {
      if (this.guardarValid) {
        let me = this;
        axios
          .post("api/Facturacion/realizarCalculosNotaCredito", this.lineasDetalle)
          .then(function (response) {
            me.lineasDetalle = response.data.comprobanteDetalle;
            me.totales = response.data.comprobante;
            
            // Assign both raw numbers and formatted strings
            me.editedItem.subtotalStr = me.totales.subtotalStr;
            me.editedItem.totalDescuentosStr = me.totales.totalDescuentosStr;
            me.editedItem.totalImpuestoStr = me.totales.totalImpuestoStr;
            me.editedItem.totalComprobanteStr = me.totales.totalComprobanteStr;
            
            me.editedItem.subtotal = me.totales.subtotal;
            me.editedItem.totalDescuentos = me.totales.totalDescuentos;
            me.editedItem.totalImpuesto = me.totales.totalImpuesto;
            me.editedItem.totalComprobante = me.totales.totalComprobante;

            // Merged mapping from redundant version
            me.editedItem.totalServGravados = me.totales.totalServGravados;
            me.editedItem.totalServExentos = me.totales.totalServExentos;
            me.editedItem.totalServExonerado = me.totales.totalServExonerado;
            me.editedItem.totalMercanciasGravadas = me.totales.totalMercanciasGravadas;
            me.editedItem.totalMercanciasExentas = me.totales.totalMercanciasExentas;
            me.editedItem.totalMercExonerada = me.totales.totalMercExonerada;
            me.editedItem.totalGravado = me.totales.totalGravado;
            me.editedItem.totalExento = me.totales.totalExento;
            me.editedItem.totalExonerado = me.totales.totalExonerado;
            me.editedItem.totalVenta = me.totales.totalVenta;
            me.editedItem.totalVentaNeta = me.totales.totalVentaNeta;
            me.editedItem.totalIVADevuelto = me.totales.totalIVADevuelto;
            me.editedItem.totalOtrosCargos = me.totales.totalOtrosCargos;
            me.editedItem.totalIva1 = me.totales.totalIva1;
            me.editedItem.totalIva2 = me.totales.totalIva2;
            me.editedItem.totalIva4 = me.totales.totalIva4;
            me.editedItem.totalIva13 = me.totales.totalIva13;

            me.editedItem.totalIva1Str = me.totales.totalIva1Str;
            me.editedItem.totalIva2Str = me.totales.totalIva2Str;
            me.editedItem.totalIva4Str = me.totales.totalIva4Str;
            me.editedItem.totalIva13Str = me.totales.totalIva13Str;
            me.editedItem.totalIVADevueltoStr = me.totales.totalIVADevueltoStr;
          })
          .catch(function (error) {
            console.log(error);
          });
      }
    },

    signalChange: function (evt) {
      this.snackText = "Si llega al escribir" + evt;
      this.snackValue = true;
    },

    changeText() {
      this.iconVisible = true;
    },

    activarIconoCedula() {
      this.iconVisibleCedula = true;
    },

    /* limpiar() {
      this.snackValue = false;
    }, */

    getData() {
      let me = this;
      axios
        .get(
          "api/Facturacion/GetComprobantes?cia=" +
            this.strCia +
            "&docNombre=NotasCredito&tipoDoc=03" +
            "&fechaInicio=" +
            this.dates[0].toString() +
            "&fechaFin=" +
            this.dates[1].toString(),
          {
            crossdomain: true,
          }
        )
        .then(function (response) {
          me.items = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    setDatesFormat() {
      //eslint-disable-next-line
      var date = new Date(
        this.dates[0].toString().replace(/-/g, "/").replace(/T.+/, "")
      );

      //eslint-disable-next-line
      var date2 = new Date(
        this.dates[1].toString().replace(/-/g, "/").replace(/T.+/, "")
      );

      var year = date2.getFullYear();
      var month = date2.getMonth() + 1;
      var dt = date2.getDate();

      if (dt < 10) {
        dt = "0" + dt;
      }
      if (month < 10) {
        month = "0" + month;
      }

      var yearInicio = date.getFullYear();
      var monthInicio = date.getMonth() + 1;
      var dtInicio = date.getDate();

      if (dtInicio < 10) {
        dtInicio = "0" + dtInicio;
      }
      if (monthInicio < 10) {
        monthInicio = "0" + monthInicio;
      }

      // this.snackText = "llega antes de sumar" + this.dates[0] + "-" + this.dates[1] + "luego de sumar: " + dtInicio + '-' + monthInicio + '-' + yearInicio + "-" + dt + '-' + month + '-' + year;
      // this.snackValue = true;

      this.datesFormat[0] = dtInicio + "-" + monthInicio + "-" + yearInicio;
      this.datesFormat[1] = dt + "-" + month + "-" + year;
    },

    setDates() {
      var date = new Date();
      //var dateInicio = new Date(date.getFullYear(), date.getMonth(), 1);
      var dateInicio = new Date(date.getFullYear(), date.getMonth(), 1);
      //var dateFinal = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      this.dates[0] = dateInicio.toISOString().substr(0, 10);
      this.dates[1] = date.toISOString().substr(0, 10);

      var year = date.getFullYear();
      var month = date.getMonth() + 1;
      var dt = date.getDate();

      if (dt < 10) {
        dt = "0" + dt;
      }
      if (month < 10) {
        month = "0" + month;
      }

      var yearInicio = dateInicio.getFullYear();
      var monthInicio = dateInicio.getMonth() + 1;
      var dtInicio = dateInicio.getDate();

      if (dtInicio < 10) {
        dtInicio = "0" + dtInicio;
      }
      if (monthInicio < 10) {
        monthInicio = "0" + monthInicio;
      }

      // this.snackText = "llega" + dt + '-' + month + '-' + year;
      // this.snackValue = true;

      this.datesFormat[0] = dtInicio + "-" + monthInicio + "-" + yearInicio;
      this.datesFormat[1] = dt + "-" + month + "-" + year;
    },

    printDate: function () {
      return new Date().tolocal;
    },

    getActividades() {
      let me = this;
      axios
        .get("api/General/getActividadesByCia?cia=" + this.strCia)
        .then(function (response) {
          me.actividades = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getCondicionVenta() {
      let me = this;
      axios
        .get("api/General/getCondicionVenta")
        .then(function (response) {
          me.condicionesVenta = response.data;
          me.editedItem.condicionVenta = me.condicionesVenta[0]["codigo"];
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getMedioPago() {
      let me = this;
      axios
        .get("api/General/getMediosPago")
        .then(function (response) {
          me.mediosPago = response.data;
          me.editedItem.medioPago = me.mediosPago[0]["codigo"];
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getCia() {
      let me = this;
      axios
        .get("api/Cias/getCia?ciaId=" + this.strCia)
        .then(function (response) {
          me.ciaItem = response.data;
          me.editedItem.emisorNombre = me.ciaItem.nombre;
          me.editedItem.emisorNombreComercial = me.ciaItem.nombreComercial;
          me.editedItem.codigoActividad = me.ciaItem.codigoActividad;
          me.editedItem.emisorTelefono = me.ciaItem.telefono;
          me.editedItem.emisorCorreo = me.ciaItem.correo;
          me.editedItem.emisorDireccion = me.ciaItem.direccion;
          me.editedItem.moneda = me.ciaItem.moneda;
          me.editedItem.cia = me.ciaItem.cedula;
          me.editedItem.fecha = me.printDate();

          me.getSucursales();
          me.getTerminales(me.ciaItem.sucursal);
          me.editedItem.sucursal = me.ciaItem.sucursal;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getSucursales() {
      let me = this;
      axios
        .get("api/Cias/getSucursalesByCia?cia=" + this.strCia)
        .then(function (response) {
          me.sucursales = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getTerminales(sucursal) {
      let me = this;
      axios
        .get(
          "api/Cias/getTerminales?cia=" + this.strCia + "&sucursal=" + sucursal
        )
        .then(function (response) {
          me.terminales = response.data;
          me.editedItem.terminal = me.terminales[0]["terminalId"];
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getBarrios() {
      let me = this;
      axios
        .get(
          "api/General/getBarrios?provincia=" +
            this.editedItem.clienteProvincia +
            "&canton=" +
            this.editedItem.clienteCanton +
            "&distrito=" +
            this.editedItem.clienteDistrito
        )
        .then(function (response) {
          me.barrios = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    changeDistritos() {
      this.getBarrios();
    },

    getDistritos() {
      let me = this;
      axios
        .get(
          "api/General/getDistritos?provincia=" +
            this.editedItem.clienteProvincia +
            "&canton=" +
            this.editedItem.clienteCanton
        )
        .then(function (response) {
          me.distritos = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    changeCantones() {
      this.getDistritos();
    },

    getCantones() {
      let me = this;
      axios
        .get(
          "api/General/getCantones?provincia=" +
            this.editedItem.clienteProvincia
        )
        .then(function (response) {
          me.cantones = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    changeProvincia() {
      this.getCantones();
    },

    changeSucursal() {
      this.getTerminales();
    },

    getProvincias() {
      let me = this;
      axios
        .get("api/General/getProvincias")
        .then(function (response) {
          me.provincias = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getMonedas() {
      let me = this;
      axios
        .get("api/General/getMonedas")
        .then(function (response) {
          me.monedas = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    // BuscarClienteCodigo() {
    //   let me = this;
    //   if (me.editedItem.clienteCod !== "") {
    //     me.showNewData = false;
    //     axios
    //       .get(
    //         "api/Clientes/getCliente?cia=" +
    //           this.strCia +
    //           "&codigo=" +
    //           this.editedItem.clienteCod
    //       )
    //       .then(function (response) {
    //         me.cliente = response.data;
    //         me.showNewData = false;
    //         me.dialog = false;
    //         me.dialogArticulos = false;
    //         me.editedItem.clienteCod = me.cliente.codigo;
    //         me.editedItem.clienteNombre = me.cliente.nombre;
    //         me.editedItem.clientecorreo = me.cliente.correo;
    //         me.editedItem.clienteCedula = me.cliente.cedula;
    //         me.editedItem.clienteAreaTel = me.cliente.areaTel;
    //         me.editedItem.clienteTelefono = me.cliente.telefono;
    //         me.editedItem.clienteNombreComercial = me.cliente.nombreComercial;
    //         me.getProvincias();
    //         me.editedItem.clienteProvincia = me.cliente.provincia;
    //         me.getCantones();
    //         me.editedItem.clienteCanton = me.cliente.canton;
    //         me.getDistritos();
    //         me.editedItem.clienteDistrito = me.cliente.distrito;
    //         me.getBarrios();
    //         me.editedItem.clienteBarrio = me.cliente.barrio;
    //         me.editedItem.clienteDireccion = me.cliente.direccion;
    //         me.editedItem.clienteTipoId = me.cliente.tipoId;
    //       })
    //       .catch(function (error) {
    //         console.log(error);
    //       });
    //   }
    // },

    // BuscarClienteCedula() {
    //   let me = this;
    //   if (me.editedItem.clienteCedula !== "") {
    //     me.showNewData = false;
    //     axios
    //       .get(
    //         "api/Clientes/getCliente?cia=" +
    //           this.strCia +
    //           "&cedula=" +
    //           this.editedItem.clienteCedula
    //       )
    //       .then(function (response) {
    //         me.cliente = response.data;
    //         me.showNewData = false;
    //         me.dialog = false;
    //         me.dialogArticulos = false;
    //         me.editedItem.clienteCod = me.cliente.codigo;
    //         me.editedItem.clienteNombre = me.cliente.nombre;
    //         me.editedItem.clientecorreo = me.cliente.correo;
    //         me.editedItem.clienteCedula = me.cliente.cedula;
    //         me.editedItem.clienteAreaTel = me.cliente.areaTel;
    //         me.editedItem.clienteTelefono = me.cliente.telefono;
    //         me.editedItem.clienteNombreComercial = me.cliente.nombreComercial;
    //         me.getProvincias();
    //         me.editedItem.clienteProvincia = me.cliente.provincia;
    //         me.getCantones();
    //         me.editedItem.clienteCanton = me.cliente.canton;
    //         me.getDistritos();
    //         me.editedItem.clienteDistrito = me.cliente.distrito;
    //         me.getBarrios();
    //         me.editedItem.clienteBarrio = me.cliente.barrio;
    //         me.editedItem.clienteDireccion = me.cliente.direccion;
    //         me.editedItem.clienteTipoId = me.cliente.tipoId;
    //       })
    //       .catch(function (error) {
    //         console.log(error);
    //       });
    //   }
    // },



    // mostrarBuscarArticulos() {
    //   this.dialogArticulos = false;
    //   this.dialogBuscarArticulos = true;
    //   this.showNewDataArti = true;
    //   this.cancelarValid = true;
    // },

    getClienteHacienda() {
      let me = this;
      axios
        .get(
          "https://api.hacienda.go.cr/fe/ae?identificacion=" +
            me.editedItem.cedula
        )
        .then(function (response) {
          me.clienteHacienda = response.data;
          me.editedItem.nombre = me.clienteHacienda.nombre;
          me.editedItem.nombreComercial = me.clienteHacienda.nombre;
          me.editedItem.tipoId = me.clienteHacienda.tipoIdentificacion;

          if (me.clienteHacienda.situacion.estado === "Inscrito") {
            me.editedItem.esProd = true;
            me.editedItem.areaTel = "506";
          } else {
            me.editedItem.esProd = false;
          }
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    changeMonedas() {
      if (this.editedItem.moneda === "USD") {
        this.getTipoCambio();
      }
      if (this.editedItem.moneda === "CRC") {
        this.editedItem.tipoCambio = 0;
      }
    },

    imprimir() {
      this.snackValue = false;
      axios
        .get(
          "api/Facturacion/getPdf?cia=" +
            this.strCia +
            "&clave=" +
            this.editedItem.clavehacienda
        )
        .then(function (response) {
          if (response.data) {
            console.log(response.data);
            window.open(response.data + ".pdf", "_blank");
          }
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getTipoCambio() {
      let me = this;
      axios
        .get(
          "https://apis.gometa.org/tdc/tdc.json?fbclid=IwAR0ye7K1k---y1DMcYsWN7AMSpQAB_LPTcbQnSDY72FrnffmeR_zfDfYiO4",
          { crossdomain: true }
        )
        .then(function (response) {
          me.tipoCambio = response.data;
          me.editedItem.tipoCambio = me.tipoCambio.venta;
        })
        .catch(function (error) {
          console.log(error);
        });
    },



    detalleEditItem(item) {
      if (this.guardarValid) {
        this.detalleEditedIndex = this.lineasDetalle.indexOf(item);
        this.detalleEditedItem = Object.assign({}, item);
        this.dialogArticulos = true;
      }
    },

    closeDelete() {
      this.snackValue = false;
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.detalleEditedItem = Object.assign({}, this.detalleDefaultItem);
        this.detalleEditedIndex = -1;
      });
    },



    deleteItemConfirm() {
      if (this.guardarValid) {
        this.lineasDetalle.splice(this.detalleEditedIndex, 1);
        this.closeDelete();

        let me = this;

        axios
          .post(
            "api/Facturacion/realizarCalculosNotaCredito",
            this.lineasDetalle
          )
          .then(function (response) {
            me.lineasDetalle = response.data.comprobanteDetalle;

            me.totales = response.data.comprobante;
            me.editedItem.totalIVADevuelto = 0; //validar esto
            me.editedItem.subTotal = me.totales.subtotal;
            me.editedItem.totalDescuentos = me.totales.totalDescuentos;
            me.editedItem.totalImpuesto = me.totales.totalImpuesto;
            me.editedItem.totalIva1 = me.totales.totalIva1;
            me.editedItem.totalIva2 = me.totales.totalIva2;
            me.editedItem.totalIva4 = me.totales.totalIva4;
            me.editedItem.totalIva13 = me.totales.totalIva13;
            me.editedItem.totalComprobante = me.totales.totalComprobante;

            me.editedItem.totalServGravados = me.totales.totalServGravados;
            me.editedItem.totalServExentos = me.totales.totalServExentos;
            me.editedItem.totalServExonerado = me.totales.totalServExonerado;
            me.editedItem.totalMercanciasGravadas =
              me.totales.totalMercanciasGravadas;
            me.editedItem.totalMercanciasExentas =
              me.totales.totalMercanciasExentas;
            me.editedItem.totalMercExonerada = me.totales.totalMercExonerada;
            me.editedItem.totalGravado = me.totales.totalGravado;
            me.editedItem.totalExento = me.totales.totalExento;
            me.editedItem.totalExonerado = me.totales.totalExonerado;
            me.editedItem.totalVenta = me.totales.totalVenta;
            me.editedItem.totalVentaNeta = me.totales.totalVentaNeta;
            me.editedItem.totalIVADevuelto = me.totales.totalIVADevuelto;
            me.editedItem.totalOtrosCargos = me.totales.totalOtrosCargos;
            me.editedItem.totalIva13 = me.totales.totalIva13;

            me.editedItem.totalIVADevueltoStr = me.totales.totalIVADevueltoStr; //validar esto
            me.editedItem.totalIva1Str = me.totales.totalIva1Str;
            me.editedItem.totalIva2Str = me.totales.totalIva2Str;
            me.editedItem.totalIva4Str = me.totales.totalIva4Str;
            me.editedItem.totalIva13Str = me.totales.totalIva13Str;
            me.editedItem.totalDescuentosStr = me.totales.totalDescuentosStr;
            me.editedItem.totalImpuestoStr = me.totales.totalImpuestoStr;
            me.editedItem.subtotalStr = me.totales.subtotalStr;
            me.editedItem.totalComprobanteStr = me.totales.totalComprobanteStr;
            //me.getData();
          })
          .catch(function (error) {
            console.log(error);
            // me.saved(false);
          });
      }
    },

    consultarEstadoHacienda() {
      this.visibleProgress = true;
      let me = this;

      this.comprobante = this.editedItem;

      axios
        .post("api/Facturacion/obtenerRespuestaHacienda", this.comprobante)
        .then(function (response) {
          me.respuestaHacienda = response.data;
          me.editedItem.estadoHacienda = me.respuestaHacienda.eInvoiceStatus;
          me.editedItem.mensajeHacienda = me.respuestaHacienda.details;
          me.visibleProgress = false;

          if (me.editedItem.estadoHacienda !== "PROCESANDO") {
            me.consultarEstadoValid = false;
          }

          if (
            me.editedItem.estadoHacienda === "ACEPTADO" ||
            me.editedItem.estadoHacienda === "RECHAZADO"
          ) {
            me.imprimirValid = true;
            me.reenviarValid = true;
            me.verPDF = true;
            me.verXMLFirmado = true;
            me.verXMLRespuesta = true;
          } else {
            me.imprimirValid = false;
            me.reenviarValid = false;
            me.verPDF = false;
            me.verXMLFirmado = false;
            me.verXMLRespuesta = false;
          }
          me.getData();
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    reenviar() {
      this.reenvioInfo.correo = this.editedItem.clientecorreo;
      this.dialogFormaReenvio = true;
    },

    confirmarReenvio() {
      this.visibleProgress = true;
      let me = this;
      this.editedItem.clientecorreo = this.reenvioInfo.correo;
      axios.post("api/Facturacion/reenviarCorreo", this.editedItem)
        .then(function (response) {
          me.snackText = response.data;
          me.snackValue = true;
          me.dialogFormaReenvio = false;
        })
        .catch(function (error) {
          console.log(error);
        })
        .finally(() => this.visibleProgress = false);
    },

    openDocViewer(url, title) {
      this.docViewerUrl = url;
      this.docViewerTitle = title;
      this.docViewerDialog = true;
    },

    abrirEnNuevaPestana() {
      window.open(this.docViewerUrl, '_blank');
    },

    getReport() {
      const base = axios.defaults.baseURL;
      this.openDocViewer(
        `${base}/api/Facturacion/getFileContent?cia=${this.strCia}&clave=${this.editedItem.clavehacienda}&type=pdf`,
        "PDF — Nota de Crédito"
      );
    },

    getXMLFirmado() {
      const base = axios.defaults.baseURL;
      this.openDocViewer(
        `${base}/api/Facturacion/getFileContent?cia=${this.strCia}&clave=${this.editedItem.clavehacienda}&type=signed`,
        "XML Enviado — Nota de Crédito"
      );
    },

    getXMLRespuesta() {
      const base = axios.defaults.baseURL;
      this.openDocViewer(
        `${base}/api/Facturacion/getFileContent?cia=${this.strCia}&clave=${this.editedItem.clavehacienda}&type=response`,
        "Respuesta XML — Nota de Crédito"
      );
    },

    getModeloComprobante() {
      let me = this;

      axios
        .get(
          "api/Facturacion/getModeloComprobanteNC?cia=" +
            this.strCia +
            "&clave=" +
            this.editedItem.codigoDev +
            "&docNombre=" +
            this.editedItem.NombreTablaReferencia
        )
        .then(function (response) {
          me.modelFactura = response.data;

          //me.editedItem = me.modelFactura.comprobante;
          me.lineasDetalle = me.modelFactura.comprobanteDetalle;

          me.editedItem.totalIVADevueltoStr =
            me.modelFactura.comprobante.totalIVADevueltoStr; //validar esto
          me.editedItem.totalIva1Str = me.modelFactura.comprobante.totalIva1Str;
          me.editedItem.totalIva2Str = me.modelFactura.comprobante.totalIva2Str;
          me.editedItem.totalIva4Str = me.modelFactura.comprobante.totalIva4Str;
          me.editedItem.totalIva13Str =
            me.modelFactura.comprobante.totalIva13Str;
          me.editedItem.totalDescuentosStr =
            me.modelFactura.comprobante.totalDescuentosStr;
          me.editedItem.totalImpuestoStr =
            me.modelFactura.comprobante.totalImpuestoStr;
          me.editedItem.subtotalStr = me.modelFactura.comprobante.subtotalStr;
          me.editedItem.totalComprobanteStr =
            me.modelFactura.comprobante.totalComprobanteStr;
          me.editedItem.enviado = 0;

          me.changeCantidad();
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getModeloComprobanteNC() {
      let me = this;

      axios
        .get(
          "api/Facturacion/getModeloComprobanteNC?cia=" +
            this.strCia +
            "&clave=" +
            this.editedItem.clavehacienda +
            "&docNombre=NotasCredito"
        )
        .then(function (response) {
          me.modelFactura = response.data;

          me.editedItem = me.modelFactura.comprobante;
          me.lineasDetalle = me.modelFactura.comprobanteDetalle;
          me.fecha = me.editedItem.fechaString;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    save() {
      this.snackValue = false;
      let me = this;
      this.visibleProgress = true;
      this.editedItem.fecha = new Date();
      this.editedItem.fechaHacienda = new Date();
      this.editedItem.contingenciaFecha = new Date();
      this.editedItem.cia = this.strCia;
      this.editedItem.usuario = this.user.id;

      this.modelFactura.comprobante = this.editedItem;
      this.modelFactura.comprobanteDetalle = this.lineasDetalle;

      if (this.editedIndex > -1) {
        axios
          .post("api/Facturacion/insertComprobante", this.modelFactura)
          .then(function (response) {
            me.visibleProgress = false;

            me.respuestaHacienda = response.data;

            me.editedItem.clavehacienda = me.respuestaHacienda.claveHacienda;
            me.editedItem.consHacReal = me.respuestaHacienda.consecutivoHacienda;
            me.editedItem.estadoHacienda = me.respuestaHacienda.eInvoiceStatus;
            me.editedItem.mensajeHacienda = me.respuestaHacienda.details;

            if (
              me.editedItem.estadoHacienda === "ACEPTADO" ||
              me.editedItem.estadoHacienda === "RECHAZADO"
            ) {
              me.imprimirValid = true;
              me.reenviarValid = true;
              me.verPDF = true;
              me.verXMLFirmado = true;
              me.verXMLRespuesta = true;
            } else {
              me.imprimirValid = false;
              me.reenviarValid = false;
              me.verPDF = false;
              me.verXMLFirmado = false;
              me.verXMLRespuesta = false;
            }

            if (me.editedItem.estadoHacienda === "PROCESANDO") {
              me.consultarEstadoValid = true;
            }
            me.getData();
          })
          .catch(function (error) {
            console.log(error);
          });
      } else {
        this.editedItem.tipoFact = "03";
        this.editedItem.nombreTabla = "NotasCredito";
        axios
          .post("api/Facturacion/insertComprobante", this.modelFactura)
          .then(function (response) {
            me.visibleProgress = false;
            me.respuestaHacienda = response.data;

            me.editedItem.clavehacienda = me.respuestaHacienda.claveHacienda;
            me.editedItem.consHacReal =
              me.respuestaHacienda.consecutivoHacienda;
            me.editedItem.estadoHacienda = me.respuestaHacienda.eInvoiceStatus;
            me.editedItem.mensajeHacienda = me.respuestaHacienda.details;

            if (
              me.editedItem.estadoHacienda === "ACEPTADO" ||
              me.editedItem.estadoHacienda === "RECHAZADO"
            ) {
              me.imprimirValid = true;
              me.reenviarValid = true;
              me.verPDF = true;
              me.verXMLFirmado = true;
              me.verXMLRespuesta = true;
            } else {
              me.imprimirValid = false;
              me.reenviarValid = false;
              me.verPDF = false;
              me.verXMLFirmado = false;
              me.verXMLRespuesta = false;
            }

            if (me.editedItem.estadoHacienda === "PROCESANDO") {
              me.consultarEstadoValid = true;
            }

            me.getData();
          })
          .catch(function (error) {
            console.log(error);
          });
      }
      this.cancelarValid = false;
      this.guardarValid = false;
      this.incluirValid = true;
      this.snackValue = false;
      this.btnAgregarArti = false;
      this.btnAgregarDocumento = false;
    },

    saveDetalleArticulo() {
      if (this.detalleEditedIndex > -1) {
        Object.assign(
          this.lineasDetalle[this.detalleEditedIndex],
          this.detalleEditedItem
        );
      } else {
        this.lineasDetalle.push(this.detalleEditedItem);
      }
      this.close();

      let me = this;
      if (this.detalleEditedIndex > -1) {
        axios
          .post(
            "api/Facturacion/realizarCalculosNotaCredito",
            this.lineasDetalle
          )
          .then(function (response) {
            me.lineasDetalle = response.data.comprobanteDetalle;
            me.totales = response.data.comprobante;
            me.editedItem.totalIVADevuelto = 0; //validar esto

            me.editedItem.subTotal = me.totales.subtotal;
            me.editedItem.totalDescuentos = me.totales.totalDescuentos;
            me.editedItem.totalImpuesto = me.totales.totalImpuesto;
            me.editedItem.totalIva1 = me.totales.totalIva1;
            me.editedItem.totalIva2 = me.totales.totalIva2;
            me.editedItem.totalIva4 = me.totales.totalIva4;
            me.editedItem.totalIva13 = me.totales.totalIva13;
            me.editedItem.totalComprobante = me.totales.totalComprobante;

            me.editedItem.totalServGravados = me.totales.totalServGravados;
            me.editedItem.totalServExentos = me.totales.totalServExentos;
            me.editedItem.totalServExonerado = me.totales.totalServExonerado;
            me.editedItem.totalMercanciasGravadas =
              me.totales.totalMercanciasGravadas;
            me.editedItem.totalMercanciasExentas =
              me.totales.totalMercanciasExentas;
            me.editedItem.totalMercExonerada = me.totales.totalMercExonerada;
            me.editedItem.totalGravado = me.totales.totalGravado;
            me.editedItem.totalExento = me.totales.totalExento;
            me.editedItem.totalExonerado = me.totales.totalExonerado;
            me.editedItem.totalVenta = me.totales.totalVenta;
            me.editedItem.totalVentaNeta = me.totales.totalVentaNeta;
            me.editedItem.totalIVADevuelto = me.totales.totalIVADevuelto;
            me.editedItem.totalOtrosCargos = me.totales.totalOtrosCargos;
            me.editedItem.totalIva13 = me.totales.totalIva13;

            me.editedItem.totalIVADevueltoStr = me.totales.totalIVADevueltoStr; //validar esto
            me.editedItem.totalIva1Str = me.totales.totalIva1Str;
            me.editedItem.totalIva2Str = me.totales.totalIva2Str;
            me.editedItem.totalIva4Str = me.totales.totalIva4Str;
            me.editedItem.totalIva13Str = me.totales.totalIva13Str;
            me.editedItem.totalDescuentosStr = me.totales.totalDescuentosStr;
            me.editedItem.totalImpuestoStr = me.totales.totalImpuestoStr;
            me.editedItem.subtotalStr = me.totales.subtotalStr;
            me.editedItem.totalComprobanteStr = me.totales.totalComprobanteStr;
          })
          .catch(function (error) {
            console.log(error);
          });
      } else {
        axios
          .post(
            "api/Facturacion/realizarCalculosNotaCredito",
            this.lineasDetalle
          )
          .then(function (response) {
            me.lineasDetalle = response.data.comprobanteDetalle;

            me.totales = response.data.comprobante;
            me.editedItem.totalIVADevuelto = 0; //validar esto
            me.editedItem.subTotal = me.totales.subtotal;
            me.editedItem.totalDescuentos = me.totales.totalDescuentos;
            me.editedItem.totalImpuesto = me.totales.totalImpuesto;
            me.editedItem.totalIva1 = me.totales.totalIva1;
            me.editedItem.totalIva2 = me.totales.totalIva2;
            me.editedItem.totalIva4 = me.totales.totalIva4;
            me.editedItem.totalIva13 = me.totales.totalIva13;
            me.editedItem.totalComprobante = me.totales.totalComprobante;

            me.editedItem.totalServGravados = me.totales.totalServGravados;
            me.editedItem.totalServExentos = me.totales.totalServExentos;
            me.editedItem.totalServExonerado = me.totales.totalServExonerado;
            me.editedItem.totalMercanciasGravadas =
              me.totales.totalMercanciasGravadas;
            me.editedItem.totalMercanciasExentas =
              me.totales.totalMercanciasExentas;
            me.editedItem.totalMercExonerada = me.totales.totalMercExonerada;
            me.editedItem.totalGravado = me.totales.totalGravado;
            me.editedItem.totalExento = me.totales.totalExento;
            me.editedItem.totalExonerado = me.totales.totalExonerado;
            me.editedItem.totalVenta = me.totales.totalVenta;
            me.editedItem.totalVentaNeta = me.totales.totalVentaNeta;
            me.editedItem.totalIVADevuelto = me.totales.totalIVADevuelto;
            me.editedItem.totalOtrosCargos = me.totales.totalOtrosCargos;
            me.editedItem.totalIva13 = me.totales.totalIva13;

            me.editedItem.totalIVADevueltoStr = me.totales.totalIVADevueltoStr; //validar esto
            me.editedItem.totalIva1Str = me.totales.totalIva1Str;
            me.editedItem.totalIva2Str = me.totales.totalIva2Str;
            me.editedItem.totalIva4Str = me.totales.totalIva4Str;
            me.editedItem.totalIva13Str = me.totales.totalIva13Str;
            me.editedItem.totalDescuentosStr = me.totales.totalDescuentosStr;
            me.editedItem.totalImpuestoStr = me.totales.totalImpuestoStr;
            me.editedItem.subtotalStr = me.totales.subtotalStr;
            me.editedItem.totalComprobanteStr = me.totales.totalComprobanteStr;
          })
          .catch(function (error) {
            console.log(error);
          });
      }
      this.cancelarValid = true;
      this.imprimirValid = false;
      this.reenviarValid = false;
      this.verPDF = false;
      this.verXMLFirmado = false;
      this.verXMLRespuesta = false;
      this.guardarValid = true;
      this.incluirValid = false;
      this.snackValue = false;
      this.panelResumen = [0];
      this.consultarEstadoValid = false;
    },

    cerrar() {
      this.drawerForm = false;
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.lineasDetalle = [];
      this.incluirValid = true;
      this.guardarValid = false;
      this.cancelarValid = false;
      this.imprimirValid = false;
      this.reenviarValid = false;
      this.verPDF = false;
      this.verXMLFirmado = false;
      this.verXMLRespuesta = false;
      this.consultarEstadoValid = false;
      this.btnAgregarDocumento = false;
    },

    close() {
      this.dialogArticulos = false;
      this.$nextTick(() => {
        this.detalleEditedItem = Object.assign({}, this.detalleDefaultItem);
        this.detalleEditedIndex = -1;
      });
    },

    deleteItem(item) {
      if (this.guardarValid) {
        this.detalleEditedIndex = this.lineasDetalle.indexOf(item);
        this.detalleEditedItem = Object.assign({}, item);
        this.dialogDelete = true;
      }
    },

    closed() {
      this.showNewData = false;
      this.showNewDataArti = false;
      this.dialog = false;
      this.dialogArticulos = false;
      this.dialogFormaReenvio = false;
    },

    selectedReenvio(value) {
      this.reenvioInfo = Object.assign({}, value);
      this.reenvioInfo.numero = "506" + this.reenvioInfo.numero;

      if (this.reenvioInfo.porwhatsapp) {
        this.reenviarWhatsApp();
      }

      if (this.reenvioInfo.porcorreo) {
        this.reenviarCorreo();
      }
    },

    getActividadesClienteDefecto(cedulaCliente) {
      if (!cedulaCliente) return;
      let me = this;
      axios.get(`api/Clientes/getActividadesByCliente?cedulaCia=${this.strCia}&cedulaCliente=${cedulaCliente}`)
        .then(res => {
          me.actividadesCliente = res.data || [];
          if (res.data && res.data.length > 0) {
            const defecto = res.data.find(a => a.esDefecto) || res.data[0];
            if (!me.editedItem.clienteCodigoActividad)
              me.editedItem.clienteCodigoActividad = defecto.codigo;
          }
        });
    },

    selected(value) {
      this.getProvincias();
      this.editedItem = Object.assign({}, value);
      this.getCodigoFactura();
      this.getCantones();
      this.getDistritos();
      this.getBarrios();
      this.getFechaActual();
      this.getModeloComprobante();
      this.editedItem.mensajeHacienda = "";
      this.editedItem.estadoHacienda = "PENDIENTE";
      this.getActividadesClienteDefecto(this.editedItem.clienteCedula);
      this.panelResumen = [0];
      this.btnAgregarDocumento = false;
      //this.dialogArticulos = false;
      //   this.editedItem.clienteCod = this.cliente.codigo;
      //   this.editedItem.clienteNombre = this.cliente.nombre;
      //   this.editedItem.clientecorreo = this.cliente.correo;
      //   this.editedItem.clienteCedula = this.cliente.cedula;
      //   this.editedItem.clienteAreaTel = this.cliente.areaTel;
      //   this.editedItem.clienteTelefono = this.cliente.telefono;
      //   this.editedItem.clienteNombreComercial = this.cliente.nombreComercial;
      //   this.getProvincias();
      //   this.editedItem.clienteProvincia = this.cliente.provincia;
      //   this.getCantones();
      //   this.editedItem.clienteCanton = this.cliente.canton;
      //   this.getDistritos();
      //   this.editedItem.clienteDistrito = this.cliente.distrito;
      //   this.getBarrios();
      //   this.editedItem.clienteBarrio = this.cliente.barrio;
      //   this.editedItem.clienteDireccion = this.cliente.direccion;
      //   this.editedItem.clienteTipoId = this.cliente.tipoId;
      //this.btnAgregarArti = true;
    },

    // selectedArticulos(value) {
    //   this.articulo = Object.assign({}, value);
    //   this.showNewDataArti = false;
    //   this.dialogArticulos = true;
    //   this.detalleEditedItem.cia = this.strCia;
    //   this.detalleEditedItem.factura = this.editedItem.codigoFact;
    //   this.detalleEditedItem.tipoFact = "01";
    //   this.detalleEditedItem.numLinea = 0;
    //   this.detalleEditedItem.unidad = this.articulo.unidad;
    //   this.detalleEditedItem.descuento = 0;
    //   this.detalleEditedItem.totalLinea = 0;
    //   this.detalleEditedItem.sucursal = this.editedItem.sucursal;
    //   this.detalleEditedItem.terminal = this.editedItem.terminal;
    //   this.detalleEditedItem.tipoCodigo = "01";
    //   this.detalleEditedItem.unidadMedidaComercial = "M2";
    //   this.detalleEditedItem.impCodigo = this.articulo.codigoImp;
    //   this.detalleEditedItem.impCodigoTarifa = this.articulo.codigoTarifa;
    //   this.detalleEditedItem.impTarifa = this.articulo.tarifa;
    //   this.detalleEditedItem.porcDescuento = 0;
    //   this.detalleEditedItem.monto = this.articulo.monto;
    //   this.detalleEditedItem.descripcion = this.articulo.nombre;
    //   this.detalleEditedItem.cantidad = 1;
    //   this.detalleEditedItem.cabys = this.articulo.cabys;
    //   this.detalleEditedItem.articulo = this.articulo.codigo;
    //   this.detalleEditedItem.tipoCambio = this.editedItem.tipoCambio;
    //   this.detalleEditedItem.impuestosVentas = this.articulo.tarifa;
    //   this.detalleEditedItem.tipoProducto = this.articulo.tipoArt;
    //   this.detalleEditedItem.moneda = this.editedItem.moneda;
    // },



    onImageChange(ev) {
      this.tempFile = ev;
      this.editedItem.certificado = ev.name;
    },

    getCodigoFactura() {
      let me = this;
      axios
        .get(
          "api/Facturacion/getComprobanteConsInterno?cia=" +
            this.strCia +
            "&docNombre=NotasCredito&tipoDoc=NC"
        )
        .then(function (response) {
          me.editedItem.codigoFact = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    getFechaActual() {
      let me = this;
      axios
        .get("api/Facturacion/getFechaActualString")
        .then(function (response) {
          me.fecha = response.data;
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    validarLicencia() {
      let me = this;
      axios
        .get("api/General/licenciaValidar?cia=" + this.strCia)
        .then(function (response) {
          me.licenciaValida = response.data;
          if (me.licenciaValida.valido) {
            me.incluir();
          } else {
            me.snackText = me.licenciaValida.motivo;
            me.snackValue = true;
          }
        })
        .catch(function (error) {
          console.log(error);
        });
    },

    incluir() {
      this.snackValue = false;
      this.$nextTick(() => {
        this.editedIndex = -1;
        this.editedItem = Object.assign({}, this.defaultItem);
        this.getCodigoFactura();
        this.cancelarValid = true;
        this.imprimirValid = false;
        this.reenviarValid = false;
        this.verPDF = false;
        this.verXMLFirmado = false;
        this.verXMLRespuesta = false;
        this.guardarValid = true;
        this.incluirValid = false;
        this.consultarEstadoValid = false;
        this.getCia();
        this.getMonedas();
        this.getActividades();
        this.getCondicionVenta();
        this.getMedioPago();
        this.getSucursales();
        this.detalleEditedItem = Object.assign({}, this.detalleDefaultItem);
        this.lineasDetalle = [];
        this.modelFactura.comprobanteDetalle = [];
        this.editedItem.estadoHacienda = "PENDIENTE";
        this.panel = [0];
        this.btnAgregarArti = false;
        this.btnAgregarDocumento = true;
        this.drawerForm = true;
        //this.getFechaActual();
      });
    },
  },
};
</script>

<style scoped>
/* === NCE DESIGN SYSTEM === */
.nce-container {
  background-color: #f1f5f9;
  min-height: 100vh;
}
.theme--dark.nce-container {
  background-color: #0f172a;
}
.nce-toolbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}
.theme--dark .nce-toolbar {
  border-bottom: 1px solid rgba(255, 255, 255, 0.07) !important;
}
.nce-toolbar-title {
  font-size: 0.95rem !important;
  letter-spacing: 1.5px;
}
.nce-btn {
  border-radius: 4px !important;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-size: 0.75rem !important;
}
.nce-table-card {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

/* Tabla principal */
.nce-table { background: transparent !important; }
.nce-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
  border-bottom: none !important;
}
.nce-table >>> tbody tr { cursor: pointer; }
.nce-table >>> tbody tr:hover {
  background-color: rgba(55, 71, 79, 0.05) !important;
}
.theme--dark .nce-table >>> tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* Search inputs */
.nce-search >>> .v-input__slot {
  border-radius: 4px !important;
}

/* Drawer */
.nce-drawer >>> .v-navigation-drawer__content {
  background-color: #ffffff !important;
}
.theme--dark .nce-drawer >>> .v-navigation-drawer__content {
  background-color: #0f172a !important;
}
.nce-drawer-header {
  background-color: #37474f;
  flex-shrink: 0;
}
.nce-drawer-header--dark {
  background-color: #263238 !important;
}
.nce-drawer-footer {
  background-color: #f8fafc;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}
.nce-drawer-footer--dark {
  background-color: #1e293b !important;
  border-top: 1px solid rgba(255, 255, 255, 0.07) !important;
}

/* Tabla de detalle */
.modern-table { background: transparent !important; }
.detail-table-modern { background: transparent !important; }
.detail-table-modern >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  letter-spacing: 0.07em;
  border-bottom: none !important;
  height: 40px !important;
}
.detail-table-modern >>> tbody td {
  border-bottom: 1px solid #f1f5f9 !important;
  height: 40px !important;
}
.theme--dark .detail-table-modern >>> thead th {
  background-color: #263238 !important;
  color: #cfd8dc !important;
}
.theme--dark .detail-table-modern >>> tbody td {
  border-bottom: 1px solid #334155 !important;
}

/* Expansion panels */
.modern-panels >>> .v-expansion-panel { border: 1px solid rgba(0,0,0,0.07) !important; border-radius: 4px !important; }
.theme--dark .modern-panels >>> .v-expansion-panel { border: 1px solid rgba(255,255,255,0.07) !important; }
.modern-panels >>> .v-expansion-panel--active {
  border: 1px solid rgba(55, 71, 79, 0.3) !important;
  box-shadow: 0 4px 12px rgba(55, 71, 79, 0.07) !important;
  margin-bottom: 2px !important;
  border-radius: 4px !important;
}
.modern-panels >>> .v-expansion-panel + .v-expansion-panel { margin-top: 2px !important; }
.modern-panels >>> .v-expansion-panel-header {
  min-height: 40px !important;
  padding: 0 12px !important;
  font-size: 0.875rem !important;
  border-radius: 4px !important;
}
.modern-panels >>> .v-expansion-panel-content__wrap { padding: 4px 4px 6px !important; }

/* Inputs compactos en tabla detalle */
.compact-input >>> input {
  padding: 0px !important;
  text-align: center !important;
  font-weight: 500 !important;
  font-size: 0.9rem !important;
  height: 28px !important;
  min-width: 40px !important;
  max-width: 60px !important;
}
.compact-input >>> .v-input__slot { padding: 0 4px !important; border-radius: 4px !important; min-height: 28px !important; }
.compact-input >>> .v-input__control { min-height: 38px !important; }
.compact-input >>> input::-webkit-outer-spin-button,
.compact-input >>> input::-webkit-inner-spin-button { -webkit-appearance: none !important; margin: 0 !important; }
.theme--dark .compact-input >>> input { color: #ffffff !important; }
.theme--light .compact-input >>> input { color: #000000 !important; }
.right-input >>> input { text-align: right; font-weight: bold; }

/* Visor de documentos */
.doc-viewer-toolbar {
  background-color: #37474f;
  min-height: 44px;
  flex-shrink: 0;
}
</style>